import { ProjectTimeEntryStatus } from "@repo/server-api";
import type { ProjectTimeEntry } from "@repo/server-api";
import type { TimeRangeValue, TimeValue } from "@repo/shared/components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import type { NewTimeEntry } from "./types";
import { formatDateTimeAsUtc } from "@repo/legacy/components/helper/Index";

dayjs.extend(utc);

// TimeRangeValue → "YYYY-MM-DD HH:mm"
export const timeRangeValueToString = (
	date: string,
	timeRange: TimeRangeValue,
): { start: string; end: string } => {
	const from = timeValueToString(date, timeRange.from);
	const to = timeValueToString(date, timeRange.to);
	return { start: from, end: to };
};

// TimeValue → "YYYY-MM-DD HH:mm"
export const timeValueToString = (date: string, time: TimeValue): string => {
	const hour = time.hour ?? 0;
	const minute = time.minute ?? 0;

	// 24:00 means end of day = next day 00:00
	if (hour === 24) {
		const nextDay = dayjs(date).add(1, "day").format("YYYY-MM-DD");
		return `${nextDay} 00:00`;
	}

	const dateString = dayjs(date).format("YYYY-MM-DD");
	return `${dateString} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm:ssZ" → TimeValue
export const stringToTimeValue = (datetime: string): TimeValue => {
	// Interpret as UTC to avoid timezone issues
	const d = dayjs.utc(datetime);

	return { hour: d.hour(), minute: d.minute() };
};

// "YYYY-MM-DD HH:mm" → TimeRangeValue
export const stringToTimeRangeValue = (start: string, end: string): TimeRangeValue => {
	const from = stringToTimeValue(start);
	let to = stringToTimeValue(end);

	// Detect next-day midnight: end is 00:00 and end date is start date + 1 day
	const startDate = dayjs.utc(start).startOf("day");
	const endParsed = dayjs.utc(end);
	const endDate = endParsed.startOf("day");

	if (endParsed.hour() === 0 && endParsed.minute() === 0 && endDate.diff(startDate, "day") === 1) {
		to = { hour: 24, minute: 0 };
	}

	return { from, to };
};

// Validierung
export const validateTimeEntry = (entry: ProjectTimeEntry | NewTimeEntry): string | null => {
	const start = dayjs(entry.start).utc();
	const end = dayjs(entry.end).utc();

	if (!start.isValid() || !end.isValid()) {
		return "invalid-time-format";
	}

	if (end.isBefore(start)) {
		return "end-must-be-after-start";
	}

	if (!entry.projectPositionId) {
		return "project-position-required";
	}

	return null;
};

// Tage eines Monats generieren
export const getDaysInMonth = (year: number, month: number): string[] => {
	const daysCount = dayjs()
		.year(year)
		.month(month - 1)
		.daysInMonth();
	return Array.from({ length: daysCount }, (_, i) =>
		dayjs()
			.year(year)
			.month(month - 1)
			.date(i + 1)
			.format("YYYY-MM-DD"),
	);
};

// Neue Zeiterfassung erstellen
export const createNewEntry = (date: string, firstProjectPositionId: string): NewTimeEntry => {
	return {
		id: "new" + Math.random(),
		start: formatDateTimeAsUtc(dayjs(date).hour(8).minute(0).format("YYYY-MM-DD HH:mm")),
		end: formatDateTimeAsUtc(dayjs(date).hour(8).minute(0).format("YYYY-MM-DD HH:mm")),
		projectPositionId: firstProjectPositionId,
		description: "",
		status: ProjectTimeEntryStatus.Open,
		logs: [],
		history: [],
	};
};
